/**
 * PROPRIETARY AND CONFIDENTIAL
 * Copyright © 2026 Contractor Co-Pilot. All Rights Reserved.
 */

"use client";

import { logger } from "@/lib/utils/logger";
import { useCallback, useEffect, useMemo, useState } from "react";

export type SavedView<TFilters> = {
  id: string;
  name: string;
  filters: TFilters;
};

const STORAGE_PREFIX = "ccp:saved-views";

const generateId = () => {
  if (typeof crypto !== "undefined" && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export function useSavedViews<TFilters>(options: {
  storageKey: string;
  initialViews?: SavedView<TFilters>[];
}) {
  const { storageKey, initialViews = [] } = options;
  const storageId = useMemo(
    () => `${STORAGE_PREFIX}:${storageKey}`,
    [storageKey],
  );

  const [views, setViews] = useState<SavedView<TFilters>[]>(() => {
    if (typeof window === "undefined") {
      return initialViews;
    }
    try {
      const raw = window.localStorage.getItem(storageId);
      if (raw) {
        return JSON.parse(raw) as SavedView<TFilters>[];
      }
    } catch (error) {
      logger.error("Failed to parse saved views", error);
    }
    return initialViews;
  });

  useEffect(() => {
    if (typeof window === "undefined") return;
    try {
      window.localStorage.setItem(storageId, JSON.stringify(views));
    } catch (error) {
      logger.error("Failed to persist saved views", error);
    }
  }, [storageId, views]);

  const upsertView = useCallback(
    (name: string, filters: TFilters, existingId?: string): string => {
      let targetId = existingId ?? generateId();
      setViews((prev) => {
        const next = [...prev];
        if (existingId) {
          const idx = next.findIndex((view) => view.id === existingId);
          if (idx >= 0) {
            next[idx] = { id: existingId, name, filters };
            return next;
          }
        }
        const idx = next.findIndex((view) => view.name === name);
        if (idx >= 0) {
          targetId = next[idx]?.id ?? targetId;
          if (next[idx]) {
            next[idx] = { id: targetId, name, filters };
            return next;
          }
        }
        next.push({ id: targetId, name, filters });
        return next;
      });
      return targetId;
    },
    [],
  );

  const removeView = useCallback((id: string) => {
    setViews((prev) => prev.filter((view) => view.id !== id));
  }, []);

  return {
    views,
    setViews,
    upsertView,
    removeView,
  };
}
